import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { useCurrentUser } from '@/stores/useCurrentUser';
import getAddresses from '@/utils/apiRestCalls/getAddresses';
import getBuildings from '@/utils/apiRestCalls/getBuildings';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { useDebounce } from '@/utils/useDebounce';
import type { Address } from '@/utils/types/address.interface';
import type { Building } from '@/utils/types/building.interface';

export const mapAddressToBuilding = (address: Address): Building => ({
  id: address.monolith_building_id,
  monolith_building_id: address.monolith_building_id,
  address_id: address.id,
  property_id: address.property?.id ?? '',
  name: address.address_line_1,
  property_name: address.property?.name ?? '',
  street: address.address_line_2 ?? '',
  city: address.city,
  state: address.state,
  zipCode: address.zip_code,
  enableManualDSDRInvitation: false,
  source: address.property?.source ?? '',
});

const mapAddressesToBuildings = (addresses: Address[]): Building[] =>
  addresses.map(mapAddressToBuilding);

const DEBOUNCE_DELAY = 500;

export const useAddresses = () => {
  const { isFeatureFlagEnabled, isReady } = useSplitFeatureFlag(
    SplitFeatureFlag.MONARCH_446_SWAP_BUILDINGS_TO_PROPERTY_API,
  );

  const { user } = useCurrentUser();
  const isNewPropertyApi = isReady && isFeatureFlagEnabled;

  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, DEBOUNCE_DELAY);

  const legacyBuildingsQuery = useQuery({
    queryKey: ['buildings'],
    queryFn: getBuildings,
    enabled: isReady && !isFeatureFlagEnabled,
    select: (buildings) => buildings.map((b) => ({ ...b, monolith_building_id: b.id })),
  });

  const addressesQuery = useQuery({
    queryKey: ['addresses', user?.id, debouncedSearch],
    queryFn: () => getAddresses(debouncedSearch ? { search: debouncedSearch } : {}),
    enabled: isNewPropertyApi,
    select: mapAddressesToBuildings,
    placeholderData: keepPreviousData,
  });

  const isDebouncing = isNewPropertyApi && searchText !== debouncedSearch && searchText.length > 0;
  const isSearchLoading =
    isDebouncing || (isNewPropertyApi && addressesQuery.isFetching && searchText.length > 0);

  const handleSearchChange = useCallback(
    (value: string) => {
      if (isNewPropertyApi) setSearchText(value.trim());
    },
    [isNewPropertyApi],
  );

  const isInitialLoading = isNewPropertyApi
    ? addressesQuery.isLoading && searchText.length === 0
    : legacyBuildingsQuery.isLoading;

  return {
    addresses: isNewPropertyApi ? addressesQuery.data : legacyBuildingsQuery.data,
    isLoading: !isReady || isInitialLoading,
    isSearchLoading,
    isNewPropertyApi,
    onSearchChange: handleSearchChange,
  };
};
